import React, {useEffect, useMemo, useState} from 'react';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {
    createSimilarityEdge,
    deleteSimilarityEdge,
    listIcrs,
    resolveIcrNumber,
} from '../../../firebase/firestore/icr';
import {listEeis, searchFintelGaps} from '../../../firebase/firestore/eei';
import {TYPE_COLORS} from './similarityLabels';
import {suggestedEdgeForTarget, targetKey, useBlockedTargetKeys} from './similarityTargets';

const TARGET_TYPES = [
    {value: 'ICR', label: 'ICR'},
    {value: 'EEI', label: 'EEI'},
    {value: 'GAP', label: 'Gap'},
];

function candidateFromIcr(icr) {
    const number = resolveIcrNumber(icr) || icr?.id || '';
    return {
        type: 'ICR',
        id: icr?.id,
        primary: number,
        secondary: icr?.title || '',
        search: `${number} ${icr?.title || ''}`.toLowerCase(),
    };
}

function candidateFromEei(eei) {
    const number = eei?.eeiNumber || eei?.number || '';
    return {
        type: 'EEI',
        id: eei?.id,
        primary: number || 'EEI',
        secondary: eei?.text || eei?.title || '',
        search: `${number} ${eei?.text || eei?.title || ''}`.toLowerCase(),
    };
}

function candidateFromGap(gap) {
    return {
        type: 'GAP',
        id: gap?.id,
        primary: gap?.text || gap?.gapText || gap?.title || 'Gap',
        secondary: gap?.reportTitle ? `FINTEL: ${gap.reportTitle}` : '',
        search: `${gap?.text || gap?.gapText || ''} ${gap?.reportTitle || ''}`.toLowerCase(),
    };
}

/**
 * Add Similar dialog: search a target entity and create a manual
 * FULL/PARTIAL similarity link with an optional comment (CM/admin).
 * The link takes effect immediately as confirmed.
 */
export default function ManualLinkDialog({open, subject, existingEdges = [], onClose, onCreated}) {
    const [targetType, setTargetType] = useState('ICR');
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [relation, setRelation] = useState('FULL');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const blocked = useBlockedTargetKeys(existingEdges);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const timer = setTimeout(async () => {
            try {
                let list = [];
                if (targetType === 'ICR') {
                    const all = await listIcrs(500);
                    const q = query.trim().toLowerCase();
                    list = (all || [])
                        .map(candidateFromIcr)
                        .filter((c) => !q || c.search.includes(q));
                } else if (targetType === 'EEI') {
                    const data = await listEeis({
                        q: query.trim() || undefined,
                        status: 'Active',
                        page: 0,
                        size: 20,
                    });
                    list = (data?.items || data?.content || data?.eeis || []).map(candidateFromEei);
                } else {
                    const data = await searchFintelGaps({q: query.trim(), limit: 20});
                    list = (data?.gaps || []).map(candidateFromGap);
                }
                list = list.filter((c) => {
                    if (!c.id) return false;
                    // The subject cannot be linked to itself
                    if (c.type === subject?.type && String(c.id) === String(subject?.id)) return false;
                    return !blocked.has(targetKey(c.type, c.id));
                }).slice(0, 20);
                if (!cancelled) setCandidates(list);
            } catch (e) {
                if (!cancelled) {
                    setCandidates([]);
                    setError(e?.message || 'Search failed');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 300 : 0);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, targetType, query, subject, blocked]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setSelected(null);
            setRelation('FULL');
            setComment('');
            setError(null);
        }
    }, [open]);

    const canSubmit = useMemo(
        () => Boolean(subject?.type && subject?.id && selected?.id && relation && !saving),
        [subject, selected, relation, saving],
    );

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        try {
            // Backend conflicts on non-rejected pairs; remove suggested machine edge first.
            const suggestion = suggestedEdgeForTarget(existingEdges, selected.type, selected.id);
            if (suggestion?.id) {
                await deleteSimilarityEdge(suggestion.id);
            }
            const body = {
                fromType: subject.type,
                fromId: subject.id,
                toType: selected.type,
                toId: selected.id,
                relation,
            };
            const trimmed = comment.trim();
            if (trimmed) body.comment = trimmed;
            await createSimilarityEdge(body);
            if (onCreated) await onCreated();
            if (onClose) onClose();
        } catch (e) {
            setError(e?.message || 'Failed to create link');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Similar</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={targetType}
                            onChange={(_, v) => {
                                if (!v) return;
                                setTargetType(v);
                                setSelected(null);
                            }}
                            sx={{flexShrink: 0}}
                        >
                            {TARGET_TYPES.map((t) => (
                                <ToggleButton
                                    key={t.value}
                                    value={t.value}
                                    sx={{textTransform: 'none', px: 1.5, whiteSpace: 'nowrap'}}
                                >
                                    {t.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            inputProps={{'aria-label': 'Search target'}}
                        />
                    </Stack>

                    {error && (
                        <Typography variant="caption" color="error">{error}</Typography>
                    )}

                    {loading ? (
                        <Stack alignItems="center" sx={{py: 2}}>
                            <CircularProgress size={20}/>
                        </Stack>
                    ) : (
                        <List dense sx={{maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                            {candidates.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 1.5}}>
                                    No matches.
                                </Typography>
                            )}
                            {candidates.map((c) => (
                                <ListItemButton
                                    key={`${c.type}:${c.id}`}
                                    selected={selected?.id === c.id && selected?.type === c.type}
                                    onClick={() => setSelected(c)}
                                >
                                    <Chip
                                        label={c.type}
                                        size="small"
                                        sx={{
                                            mr: 1,
                                            bgcolor: `${TYPE_COLORS[c.type] || '#757575'}22`,
                                            color: TYPE_COLORS[c.type] || 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                    />
                                    <ListItemText
                                        primary={c.primary}
                                        secondary={c.secondary || undefined}
                                        primaryTypographyProps={{variant: 'body2'}}
                                        secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">Overlap</Typography>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={relation}
                            onChange={(_, v) => v && setRelation(v)}
                        >
                            <ToggleButton value="FULL" sx={{textTransform: 'none', px: 1.5}}>Full</ToggleButton>
                            <ToggleButton value="PARTIAL" sx={{textTransform: 'none', px: 1.5}}>Partial</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        label="Comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Optional note about this link"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" sx={{textTransform: 'none'}}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={submit}
                    sx={{textTransform: 'none'}}
                >
                    Link
                </Button>
            </DialogActions>
        </Dialog>
    );
}
